import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Rating,
  Avatar,
  Divider,
  Button,
  Grid,
  LinearProgress,
} from '@mui/material';
import { Reviews, Star } from '@mui/icons-material';
import { CaretakerProfile } from '../services/caretakerProfileService';

interface CaretakerReviewsProps {
  reviews: CaretakerProfile['reviews'];
  averageRating: number;
  reviewCount: number;
  ratingBreakdown: CaretakerProfile['ratingBreakdown'];
}

const CaretakerReviews: React.FC<CaretakerReviewsProps> = ({
  reviews,
  averageRating,
  reviewCount,
  ratingBreakdown
}) => {
  const [showAllReviews, setShowAllReviews] = useState(false);
  const reviewsToShow = showAllReviews ? (reviews || []) : (reviews || []).slice(0, 5);

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getPatientInitials = (firstName: string | null, lastName: string | null): string => {
    const first = firstName ? firstName.charAt(0).toUpperCase() : '';
    const last = lastName ? lastName.charAt(0).toUpperCase() : '';
    return `${first}${last}` || 'A';
  };

  const getTotalReviews = (): number => {
    return Object.values(ratingBreakdown).reduce((sum, count) => sum + count, 0);
  };

  const getPercentageForRating = (rating: number): number => {
    const total = getTotalReviews();
    return total > 0 ? (ratingBreakdown[rating as keyof typeof ratingBreakdown] / total) * 100 : 0;
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h5" component="h2" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Reviews />
          Reviews & Ratings
        </Typography>

        {reviewCount > 0 ? (
          <>
            {/* Rating Summary */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Box textAlign="center">
                  <Typography variant="h2" component="div" color="primary">
                    {averageRating.toFixed(1)}
                  </Typography>
                  <Rating
                    value={averageRating}
                    precision={0.1}
                    readOnly
                    size="large"
                  />
                  <Typography variant="body2" color="text.secondary">
                    Based on {reviewCount} review{reviewCount !== 1 ? 's' : ''}
                  </Typography>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={8}>
                <Box>
                  {[5, 4, 3, 2, 1].map((rating) => {
                    const count = ratingBreakdown[rating as keyof typeof ratingBreakdown];
                    const percentage = getPercentageForRating(rating);
                    
                    return (
                      <Box key={rating} display="flex" alignItems="center" gap={2} mb={1}>
                        <Box display="flex" alignItems="center" minWidth={60}>
                          <Typography variant="body2" sx={{ mr: 0.5 }}>
                            {rating}
                          </Typography>
                          <Star sx={{ fontSize: 16, color: 'warning.main' }} />
                        </Box>
                        <LinearProgress
                          variant="determinate"
                          value={percentage}
                          sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
                        />
                        <Typography variant="body2" minWidth={30}>
                          {count}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            {/* Individual Reviews */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Recent Reviews
              </Typography>
              
              {reviewsToShow.map((review, index) => (
                <Box key={review.id}>
                  <Box display="flex" gap={2} mb={2}>
                    <Avatar
                      sx={{
                        width: 40,
                        height: 40,
                        bgcolor: 'primary.main'
                      }}
                    >
                      {getPatientInitials(review.patient.firstName, review.patient.lastName)}
                    </Avatar>
                    
                    <Box flexGrow={1}>
                      <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                        <Typography variant="subtitle2" fontWeight="medium">
                          {review.patient.firstName} {review.patient.lastName}
                        </Typography>
                        <Rating
                          value={review.rating}
                          readOnly
                          size="small"
                        />
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(review.createdAt)}
                        </Typography>
                      </Box>
                      
                      {review.comment && (
                        <Typography variant="body2" color="text.primary">
                          {review.comment}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  
                  {index < reviewsToShow.length - 1 && <Divider sx={{ my: 2 }} />}
                </Box>
              ))}
              
              {(reviews || []).length > 5 && (
                <Box textAlign="center" mt={2}>
                  <Button
                    variant="outlined"
                    onClick={() => setShowAllReviews(!showAllReviews)}
                  >
                    {showAllReviews ? 'Show Less' : `Show All ${reviewCount} Reviews`}
                  </Button>
                </Box>
              )}
            </Box>
          </>
        ) : (
          <Typography variant="body1" color="text.secondary">
            No reviews yet. Be the first to leave a review!
          </Typography>
        )}
      </CardContent>
    </Card>
  );
};

export default CaretakerReviews;
